import { type MetaFunction } from '@remix-run/node'
import {
	LegalPageContainer,
	LegalPageEffectiveDate,
	LegalPageLink,
	LegalPageList,
	LegalPageListItem,
	LegalPageParagraph,
	LegalPageSectionTitle,
	LegalPageTitle,
} from '#app/components/layout/legal'
import { LogoHome } from '#app/components/logo'

export const meta: MetaFunction = () => {
	return [
		{ title: 'Terms of Service | Choros' },
		{ name: 'description', content: 'Choros Terms of Service' },
	]
}

export default function TermsOfServiceRoute() {
	return (
		<LegalPageContainer>
			<LogoHome />
			<LegalPageTitle>Choros Terms of Service</LegalPageTitle>
			<LegalPageEffectiveDate>
				Effective Date: January 9th, 2025
			</LegalPageEffectiveDate>

			<LegalPageParagraph>
				Thank you for your interest in Choros ("the App"). These Terms of
				Service ("Terms") govern your access to and use of our services,
				including your participation in our Alpha testing program. By applying
				to become a test user or accessing any part of the App, you agree to be
				bound by these Terms.
			</LegalPageParagraph>

			<LegalPageSectionTitle>
				1. Overview of the Alpha Testing Program
			</LegalPageSectionTitle>
			<LegalPageParagraph>
				The Alpha version of the App is being made available as part of a
				limited testing program. Participation is by invitation only, subject to
				approval by the co-founders, and provided at no cost during the Alpha
				testing period.
			</LegalPageParagraph>

			<LegalPageSectionTitle>2. Eligibility Requirements</LegalPageSectionTitle>
			<LegalPageParagraph>
				To apply as a test user, you must:
			</LegalPageParagraph>
			<LegalPageList>
				<LegalPageListItem>Be at least 18 years old.</LegalPageListItem>
				<LegalPageListItem>
					Submit accurate and complete information as part of the application
					process.
				</LegalPageListItem>
				<LegalPageListItem>
					Agree to comply with these Terms and any additional guidelines
					provided during the testing period.
				</LegalPageListItem>
			</LegalPageList>

			<LegalPageSectionTitle>
				3. Application and Selection Process
			</LegalPageSectionTitle>
			<LegalPageParagraph>
				Submission of an application does not guarantee acceptance. The
				co-founders will review applications and notify selected applicants via
				email or other written communication. The decision to accept or reject
				an applicant is at the sole discretion of the co-founders.
			</LegalPageParagraph>

			<LegalPageSectionTitle>
				4. No Fees During Alpha Testing
			</LegalPageSectionTitle>
			<LegalPageParagraph>
				Participation in the Alpha testing program is free of charge. If
				material changes to the cost or structure of the service occur after the
				Alpha testing period, users will be notified at least 30 days in advance
				in writing.
			</LegalPageParagraph>

			<LegalPageSectionTitle>
				5. Testing Purpose and Feedback
			</LegalPageSectionTitle>
			<LegalPageParagraph>
				By participating in the Alpha program, you understand that the App is in
				development and may contain bugs, errors, or other issues. You agree to:
			</LegalPageParagraph>
			<LegalPageList>
				<LegalPageListItem>
					Provide honest and constructive feedback as requested.
				</LegalPageListItem>
				<LegalPageListItem>
					Refrain from publicly sharing any confidential or proprietary
					information about the App without written consent.
				</LegalPageListItem>
			</LegalPageList>

			<LegalPageSectionTitle>6. User Conduct</LegalPageSectionTitle>
			<LegalPageParagraph>
				As an Alpha tester, you agree to use the App responsibly and in
				accordance with all applicable laws. You must not:
			</LegalPageParagraph>
			<LegalPageList>
				<LegalPageListItem>
					Attempt to reverse engineer, hack, or otherwise compromise the App.
				</LegalPageListItem>
				<LegalPageListItem>
					Share your access with unapproved individuals.
				</LegalPageListItem>
			</LegalPageList>

			<LegalPageSectionTitle>
				7. Changes to the Alpha Program
			</LegalPageSectionTitle>
			<LegalPageParagraph>
				The Alpha testing program may be modified, suspended, or terminated at
				any time at the sole discretion of the co-founders. Users will be
				notified of any significant changes.
			</LegalPageParagraph>

			<LegalPageSectionTitle>
				8. Data Collection and Privacy
			</LegalPageSectionTitle>
			<LegalPageParagraph>
				By using the App, you consent to the collection and use of information
				as described in our Privacy Policy. We will only use your information to
				improve the App and ensure a positive user experience.
			</LegalPageParagraph>

			<LegalPageSectionTitle>9. Limitation of Liability</LegalPageSectionTitle>
			<LegalPageParagraph>
				The App is provided "as is" without any warranties. To the maximum
				extent permitted by law, Choros and its co-founders shall not be liable
				for any damages arising from your participation in the Alpha testing
				program.
			</LegalPageParagraph>

			<LegalPageSectionTitle>10. Termination</LegalPageSectionTitle>
			<LegalPageParagraph>
				Your participation in the Alpha testing program may be terminated if you
				violate these Terms. You may also opt out of the program at any time by
				providing written notice.
			</LegalPageParagraph>

			<LegalPageSectionTitle>11. Notification of Changes</LegalPageSectionTitle>
			<LegalPageParagraph>
				If material changes to these Terms or the cost structure of the App
				occur, you will be notified at least 30 days in advance via email or
				other written communication.
			</LegalPageParagraph>

			<LegalPageSectionTitle>12. Contact Information</LegalPageSectionTitle>
			<LegalPageParagraph>
				For questions or concerns about these Terms, please contact us at{' '}
				<LegalPageLink href="mailto:alex@chorosapp.com">
					alex@chorosapp.com
				</LegalPageLink>
				.
			</LegalPageParagraph>

			<LegalPageParagraph>
				By submitting your application and/or participating in the Alpha testing
				program, you acknowledge that you have read, understood, and agree to
				these Terms of Service.
			</LegalPageParagraph>
		</LegalPageContainer>
	)
}
